import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AudioButton } from '../components/AudioButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Send,
  Mic,
  MicOff,
  Volume2,
  Loader2,
  MessageCircle,
  User,
  Bot,
  Sparkles,
  Trash2,
  BarChart3,
  Award,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avatar components for tutors
const TeacherAvatar = ({ teacher, size = "md" }) => {
  const sizes = {
    sm: "w-8 h-8 text-sm",
    md: "w-10 h-10 text-base",
    lg: "w-16 h-16 text-2xl"
  };
  
  const isHerrAmr = teacher === "herr_amr";
  
  return (
    <div className={`${sizes[size]} rounded-full flex items-center justify-center ${
      isHerrAmr ? 'bg-blue-500' : 'bg-pink-500'
    }`}>
      {isHerrAmr ? '👨‍🏫' : '👩‍🏫'}
    </div>
  );
};

export default function FreeChat() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [selectedTeacher, setSelectedTeacher] = useState('frau_rasha');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('A1');
  const [topics, setTopics] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([fetchTopics(), fetchStats(), fetchHistory()]);
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/free-chat/suggested-topics`);
      setTopics(response.data.topics || []);
      setSelectedLevel(response.data.level || 'A1');
    } catch (error) {
      toast.error("Failed to load topics");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/free-chat/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load stats");
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/free-chat/history?limit=50`);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error("Failed to load chat history");
    }
  };

  const sendMessage = async (text = inputMessage) => {
    if (!text.trim() || isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: text,
      teacher: selectedTeacher,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/free-chat/message`, {
        message: text,
        teacher: selectedTeacher,
        topic: selectedTopic,
        level: selectedLevel
      });
      
      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        teacher: selectedTeacher,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      if (response.data.xp_earned > 0) {
        toast.success(`+${response.data.xp_earned} XP earned!`);
      }
      
      if (response.data.milestone) {
        toast.success(`🎉 Milestone reached: ${response.data.milestone} messages!`);
      }
      
      // Refresh stats
      fetchStats();
      
    } catch (error) {
      toast.error('Failed to send message');
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Transcribe
        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');
          formData.append('language', 'de');
          
          const response = await axios.post(`${API}/speech/transcribe`, formData);
          
          if (response.data.text) {
            sendMessage(response.data.text);
          }
        } catch (error) {
          toast.error('Could not transcribe audio');
        }
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
      // Auto-stop after 15 seconds
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
          setIsRecording(false);
        }
      }, 15000);
      
      window.currentRecorder = mediaRecorder;
      
    } catch (error) {
      toast.error('Could not access microphone');
    }
  };

  const stopVoiceRecording = () => {
    if (window.currentRecorder && window.currentRecorder.state === 'recording') {
      window.currentRecorder.stop();
      setIsRecording(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear all chat history? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/free-chat/clear`);
      setMessages([]);
      toast.success('Chat history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const getTeacherName = (teacher) => {
    return teacher === 'herr_amr' ? 'Herr Amr' : 'Frau Rasha';
  };

  const getTeacherDescription = (teacher) => {
    return teacher === 'herr_amr' 
      ? 'Grammar Expert - Precise & Methodical'
      : 'Conversation Coach - Warm & Encouraging';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col" data-testid="free-chat-page">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-md border-b border-slate-700 shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/10"
            size="sm"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-400" />
            <h1 className="text-lg font-bold text-white">Free Chat</h1>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowStats(!showStats)}
              className="text-slate-400 hover:text-white"
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearHistory}
              className="text-slate-400 hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Stats Panel (collapsible) */}
      {showStats && stats && (
        <div className="bg-slate-800/50 border-b border-slate-700 p-4">
          <div className="max-w-4xl mx-auto grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-emerald-400">{stats.total_messages}</p>
              <p className="text-xs text-slate-400">Total Messages</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-400">{stats.by_teacher?.herr_amr || 0}</p>
              <p className="text-xs text-slate-400">With Herr Amr</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-pink-400">{stats.by_teacher?.frau_rasha || 0}</p>
              <p className="text-xs text-slate-400">With Frau Rasha</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{stats.daily_xp_remaining}</p>
              <p className="text-xs text-slate-400">XP Left Today</p>
            </div>
          </div>
        </div>
      )}

      {/* Teacher Selection */}
      <div className="bg-slate-800/30 border-b border-slate-700 p-3">
        <div className="max-w-4xl mx-auto">
          <Tabs value={selectedTeacher} onValueChange={setSelectedTeacher}>
            <TabsList className="grid w-full grid-cols-2 bg-slate-800/50">
              <TabsTrigger 
                value="frau_rasha" 
                className="data-[state=active]:bg-pink-500 data-[state=active]:text-white"
              >
                <span className="mr-2">👩‍🏫</span>
                Frau Rasha
              </TabsTrigger>
              <TabsTrigger 
                value="herr_amr" 
                className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
              >
                <span className="mr-2">👨‍🏫</span>
                Herr Amr
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <p className="text-xs text-slate-400 text-center mt-2">
            {getTeacherDescription(selectedTeacher)}
          </p>
        </div>
      </div>

      {/* Topic Pills (optional) */}
      {topics.length > 0 && (
        <div className="bg-slate-800/20 p-3 border-b border-slate-700/50">
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-xs text-slate-500 shrink-0">Topic:</span>
            <Button
              variant={selectedTopic === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedTopic(null)}
              className={`h-7 text-xs shrink-0 ${selectedTopic === null ? 'bg-emerald-500' : 'text-slate-400 border-slate-600'}`}
            >
              Free Talk
            </Button>
            {topics.map((topic) => (
              <Button
                key={topic.id}
                variant={selectedTopic === topic.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedTopic(topic.id)}
                className={`h-7 text-xs shrink-0 ${selectedTopic === topic.id ? 'bg-emerald-500' : 'text-slate-400 border-slate-600'}`}
              >
                {topic.icon} {topic.name_en}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <TeacherAvatar teacher={selectedTeacher} size="lg" />
              <h3 className="text-xl font-bold text-white mt-4">
                {selectedTeacher === 'herr_amr' 
                  ? 'Guten Tag! Ich bin Herr Amr.'
                  : 'Hallo! Ich bin Frau Rasha! 😊'
                }
              </h3>
              <p className="text-slate-400 mt-2 max-w-md mx-auto">
                {selectedTeacher === 'herr_amr'
                  ? "Let's practice German together. I'll help you with grammar and structure. Feel free to ask me anything!"
                  : "Let's have a fun conversation in German! Don't worry about mistakes - just speak! Tell me about yourself!"
                }
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendMessage(selectedTeacher === 'herr_amr' 
                    ? 'Hallo Herr Amr! Können wir Grammatik üben?' 
                    : 'Hallo Frau Rasha! Ich möchte Deutsch üben.'
                  )}
                  className="text-slate-300 border-slate-600"
                >
                  Start a conversation
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendMessage('Ich heiße... und ich komme aus Ägypten.')}
                  className="text-slate-300 border-slate-600"
                >
                  Introduce yourself
                </Button>
              </div>
            </div>
          )}

          {messages.map((msg, index) => (
            <div
              key={msg.id || `chat-${index}-${msg.role}`}
              className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              {/* Avatar */}
              <div className="shrink-0">
                {msg.role === 'user' ? (
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                ) : (
                  <TeacherAvatar teacher={msg.teacher} size="md" />
                )}
              </div>
              
              {/* Message Bubble */}
              <div className={`max-w-[75%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`rounded-2xl px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-emerald-500 text-white rounded-br-sm'
                      : 'bg-slate-700 text-slate-100 rounded-bl-sm'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                
                {/* Audio button for AI messages */}
                {msg.role === 'assistant' && (
                  <div className="mt-1 flex items-center gap-1 text-slate-500">
                    <AudioButton 
                      text={msg.content} 
                      voice={msg.teacher === 'herr_amr' ? 'onyx' : 'nova'}
                      size="sm"
                      className="text-slate-500 hover:text-emerald-400"
                    />
                    <span className="text-xs">Listen</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex gap-3">
              <TeacherAvatar teacher={selectedTeacher} size="md" />
              <div className="bg-slate-700 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-slate-900/80 backdrop-blur-md border-t border-slate-700 p-4 shrink-0">
        <div className="max-w-4xl mx-auto flex gap-2">
          <Button
            variant={isRecording ? 'destructive' : 'outline'}
            size="icon"
            onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
            className={`shrink-0 ${isRecording ? 'animate-pulse' : 'border-slate-600'}`}
            data-testid="voice-record-btn"
          >
            {isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>
          
          <Input
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Message ${getTeacherName(selectedTeacher)}...`}
            className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
            disabled={isLoading}
            data-testid="chat-input"
          />
          
          <Button
            onClick={() => sendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            className="bg-emerald-500 hover:bg-emerald-600 shrink-0"
            data-testid="send-message-btn"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
        
        {isRecording && (
          <p className="text-center text-sm text-red-400 mt-2 animate-pulse">
            🎤 Recording... Tap microphone to stop
          </p>
        )}
      </div>
    </div>
  );
}
